import { useState, useEffect } from 'react';
import CloseIcon from '../assets/close.svg';
import LocateIcon from '../assets/locate-icon.svg';
import LocationIcon from '../assets/location-full-icon.svg';
import SpinnerIcon from '../assets/spinner.svg';
import SpinnerIcon2 from '../assets/spinner3.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import useToast from '../hooks/useToast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import Toast from './Toast';
import log from '../utils/logger';
import { getApiErrorMessage } from '../utils/apiError';
import api from '../api/api';
import { DEFAULT_CENTER, GEOAPIFY_COUNTRY_CODE, GEOAPIFY_RESULT_LIMIT } from '../config/appConfig';
 
const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;

const SelectLocationModal = ({ isLocationModalOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBodyScrollLock(isLocationModalOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [isHandlingSelection, setIsHandlingSelection] = useState(false);
  const [isFetchingSearch, setIsFetchingSearch] = useState(false);
  const { user, idToken, updateProfile, logout, adminImpersonate } = useUser();
  const { clearCart } = useCart();
  const { toastMessage, toastType, showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [locateClicked, setLocateClicked] = useState(false);
  const [locateOnMapClicked, setLocateOnMapClicked] = useState(false);

  useEffect(() => {
    if (isLocationModalOpen) {
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isLocationModalOpen]);

  const checkPermissionStatus = async () => {
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({
          name: 'geolocation',
        });
        return result.state; // 'granted' | 'prompt' | 'denied'
      } catch (err) {
        log.error('Select location permission error:', err);
        return null;
      }
    }
    return null;
  };

  const handleLocate = async () => {
    const permission = await checkPermissionStatus();

    if (permission === 'denied') {
      setLocationError(
        t('locationPermissionDenied'),
      );
      return;
    }

    if (!navigator.geolocation) {
      setLocationError(t('geolocationNotSupported'));
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: latitude,
              lon: longitude,
            },
          });
          const data = res.data.response.data;

          await updateUserLocation(data);

          setLocationError('');
          onClose();
        } catch (error) {
          log.error(error);
          setLocationError(
            t('addressFetchFailed'),
          );
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        log.error(error);
        setLocationError(
          t('locationRetrievalFailed'),
        );
        setIsLocating(false);
      },
    );
  };

  const handleLocateClick = () => {
    if (isLocating) {
      return;
    }

    setLocateClicked(false);
    requestAnimationFrame(() => setLocateClicked(true));

    handleLocate();
  };

  const handleLocateOnMapClick = () => {
    setLocateOnMapClicked(false);
    requestAnimationFrame(() => setLocateOnMapClicked(true));
  };

  const updateUserLocation = async (addr) => {
    const street =
      [addr.address.housenumber, addr.address.road].filter(Boolean).join(' ') ||
      '';
    const city =
      addr.address.city ||
      addr.address.county ||
      addr.address.town ||
      addr.address.village ||
      '';
    const province = addr.address.state || '';
    const postal = addr.address.postcode || '';
    const country = addr.address.country || '';
    const shortProvince = province.match(/\b[A-Z]{2}\b/)?.[0] || '';
    const formattedAddress = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

    try {
      if ((user && idToken) || adminImpersonate) {
        const payload = {
          latitude: addr.lat,
          longitude: addr.lon,
          formattedAddress,
          addressNumber: addr.address.house_number,
          city: addr.address.city,
          zone: addr.address.state,
          isEdit: false,
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        };

        const response = await api.put('/user/locations', payload, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        const locations = response.data.response.data.locations;
        updateProfile({ locations: locations });
      } else {
        const location = {
          formattedAddress,
          latitude: Number(addr.lat),
          longitude: Number(addr.lon),
        };
        updateProfile({ locations: [location] });
      }
    } catch (error) {
      log.error('Error saving address:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchSearchResults(searchQuery);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const fetchSearchResults = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsFetchingSearch(true);

      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(query)}&bias=proximity:${DEFAULT_CENTER.lat},${DEFAULT_CENTER.lng}&filter=countrycode:${GEOAPIFY_COUNTRY_CODE}&lang=fr&limit=${GEOAPIFY_RESULT_LIMIT}&apiKey=${GEOAPIFY_KEY}`,
      );

      const data = await response.json();

      const getPriority = (item) => {
        const city = (item.properties.city || '').toLowerCase();
        const state = (item.properties.state || '').toLowerCase();

        if (city === 'montreal' || city === 'montréal') {
          return 1;
        }
        if (state === 'québec' || state === 'quebec') {
          return 2;
        }
        return 3;
      };

      const sorted = data.features.sort(
        (a, b) => getPriority(a) - getPriority(b),
      );
      setSearchResults(sorted.slice(0, 5));
    } catch (error) {
      log.error('Geoapify search failed:', error);
    } finally {
      setIsFetchingSearch(false);
    }
  };

  const handleInputSelection = async (latitude, longitude) => {
    try {
      setIsHandlingSelection(true);
      const res = await api.get('/buyer/map/geocode/reverse', {
        params: {
          lat: latitude,
          lon: longitude,
        },
      });
      const data = res.data.response.data;

      await updateUserLocation(data);

      setSearchQuery('');
      setSearchResults([]);
      setLocationError('');
      onClose();
    } catch (error) {
      log.error(error);
      setLocationError(t('addressFetchFailed'));
    } finally {
      setIsHandlingSelection(false);
    }
  };

  return (
    <div>
      {/* Slide-in Modal */}
      <div
        className={`fixed z-50 bg-white transition-transform duration-300 overflow-y-auto
          bottom-0 left-0 right-0 w-full max-h-[95dvh] rounded-t-2xl
          lg:top-0 lg:right-auto lg:max-w-[550px] lg:h-full lg:max-h-full lg:rounded-none ${
    isAnimating
      ? 'translate-y-0 lg:translate-x-0'
      : 'translate-y-full lg:translate-y-0 lg:-translate-x-full'
    }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center w-full p-4 border-b-2 border-black border-opacity-10">
          <h2 className="font-medium text-xl text-[#000000de]">
            {t('searchlocation')}
          </h2>
          <button
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 hover:bg-[#e7e6e6bf] active:bg-[#aaa]"
            onClick={onClose}
          >
            <img src={CloseIcon} alt="CloseIcon" />
          </button>
        </div>

        <div className="py-6 px-4 pb-24 lg:pb-6">
          {/* Location input and Locate button */}
          <div className="input-shadow rounded-[10px] min-h-14 px-3 flex items-center gap-2 border w-full outline-none transition-all duration-200">
            <input
              type="text"
              placeholder={t('homeherinputplaceholder')}
              className="w-full h-full border-none outline-none flex-1 placeholder:font-light"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              onClick={handleLocateClick}
              disabled={isLocating}
              className="
                relative overflow-hidden
                h-9 flex items-center justify-center gap-2 px-4
                text-sm rounded font-medium uppercase
                text-[#000000DE]
                transition-colors duration-300
                hover:bg-[#e7e6e6bf]
              "
            >
              {/* Pulse effect */}
              {locateClicked && (
                <span
                  className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                  onAnimationEnd={() => setLocateClicked(false)}
                />
              )}

              {isLocating ? (
                <img
                  src={SpinnerIcon}
                  alt="Loading..."
                  className="h-5 w-5 animate-spin"
                />
              ) : (
                <>
                  <img
                    src={LocateIcon}
                    alt="LocateIcon"
                    className="h-[18px] w-[18px]"
                  />
                  <p className="hidden md:block">
                    {t('homeherinputlocatebtn')}
                  </p>
                </>
              )}
            </button>
          </div>

          <ul className="max-h-72 overflow-y-auto mt-2">
            {isHandlingSelection || isFetchingSearch ? (
              <div className="flex items-center justify-center h-32">
                <img
                  src={SpinnerIcon2}
                  alt="Loading..."
                  className="h-5 w-5 animate-spin"
                />
              </div>
            ) : (
              searchResults.map((result) => {
                const props = result.properties;
                const displayName = props.formatted;
                const [lon, lat] = result.geometry.coordinates;

                return (
                  <li
                    key={result.properties.place_id}
                    className="cursor-pointer px-3 py-2 hover:bg-gray-200 rounded"
                    onClick={() => {
                      handleInputSelection(lat, lon);
                    }}
                  >
                    {displayName}
                  </li>
                );
              })
            )}
          </ul>

          {/* Optional error message */}
          {locationError && (
            <p className="mt-3 text-red-600 text-sm">{locationError}</p>
          )}

          <p className="text-center my-4 text-xs md:text-base text-[#000000DE]">
            --&nbsp;{t('or')}&nbsp;--
          </p>

          {/* Navigate to map page */}
          <button
            onClick={() => {
              handleLocateOnMapClick();
              navigate('/service-area');
            }}
            className="
              relative overflow-hidden
              h-[52px] p-6 text-base text-[#000000DE]
              rounded w-full
              flex items-center justify-center gap-2
              font-medium uppercase
              bg-white border border-black
              transition-colors duration-300
              hover:bg-[#e7e6e6bf]
            "
          >
            {/* Pulse effect */}
            {locateOnMapClicked && (
              <span
                className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                style={{ animationDuration: '0.3s' }}
                onAnimationEnd={() => setLocateOnMapClicked(false)}
              />
            )}

            <img src={LocationIcon} alt="LocationIcon" />
            {t('setlocationmap')}
          </button>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40 ${
          isAnimating
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default SelectLocationModal;
